import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  CalendarClock,
  CalendarOff,
  Network,
  ClipboardList,
  FileText,
  Layers,
  LineChart,
  MoreHorizontal,
  Settings,
  Settings2,
  ShoppingBasket,
  Store,
  Turtle,
  User,
  LogOut,
  ChevronDown,
  GitBranch,
  GitCompareArrows,
  Megaphone,
  ShieldCheck,
  Terminal as TerminalIcon,
  Users,
  Search,
} from 'lucide-react';
import { NavDropdown, type NavItem, type NavSection } from './NavDropdown';
import ThemeToggle from '../common/ThemeToggle';
import KillSwitch from '../common/KillSwitch';
import { cn } from '../../utils/cn';
import { useAuth } from '../../context/AuthContext';
import { useTradingStatus } from '../../context/TradingStatusContext';
import { useOAuth } from '../../context/OAuthContext';
import { useActingAs } from '../../context/ActingAsContext';
import { Eye, X as XIcon } from 'lucide-react';
import { getTenantConfig, isWhiteLabel } from '../../config/tenantConfig';
import brokerService from '../../services/brokerService';
import { getPopupDimensions } from '../../config/brokerConfigs';
import { BROKER_NAME } from '../../constants/tradingConstants';
import type { BrokerAccount } from '../../types';

const Header: React.FC = () => {
  const { user, isAdmin, isAccountManager, isSuperAdmin, logout } = useAuth();
  const { tradingEnabled, setTradingEnabled } = useTradingStatus();
  const { actingAs, actingAsLabel, setActingAs, managedUsers } = useActingAs();
  const sortedManagedUsers = useMemo(
    () => [...managedUsers].sort((a, b) => {
      const labelA = (a.full_name || a.email || a.managed_client_id).toLowerCase();
      const labelB = (b.full_name || b.email || b.managed_client_id).toLowerCase();
      return labelA.localeCompare(labelB);
    }),
    [managedUsers],
  );
  const tenantConfig = getTenantConfig();
  const wl = isWhiteLabel();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [viewAsQuery, setViewAsQuery] = useState('');
  const filteredManagedUsers = useMemo(() => {
    const q = viewAsQuery.trim().toLowerCase();
    if (!q) return sortedManagedUsers;
    return sortedManagedUsers.filter((u) =>
      [u.full_name, u.email, u.managed_client_id].some((v) => v?.toLowerCase().includes(q)),
    );
  }, [sortedManagedUsers, viewAsQuery]);
  // Every close path (pick, My account, outside click, toggle) starts the
  // next open with an empty search.
  useEffect(() => {
    if (!viewAsOpen) setViewAsQuery('');
  }, [viewAsOpen]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const viewAsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { initiateAuth } = useOAuth();
  const [zebuAccount, setZebuAccount] = useState<BrokerAccount | null>(null);
  const [zebuReauthing, setZebuReauthing] = useState(false);

  const fetchZebuAccount = useCallback(() => {
    if (!wl) return;
    brokerService.getBrokerAccounts()
      .then(accounts => {
        if (accounts.length > 0) setZebuAccount(accounts[0]);
      })
      .catch(() => { /* silent — chip just won't render */ });
  }, [wl]);

  // Fetch broker account once for white-label users — visible across all pages
  useEffect(() => {
    fetchZebuAccount();
  }, [fetchZebuAccount]);

  const zebuClientId = zebuAccount?.client_id || '';
  const zebuTokenValid = !!zebuAccount &&
    zebuAccount.has_oauth_token &&
    zebuAccount.is_oauth_valid !== false &&
    !zebuAccount.requires_reauth;

  const handleZebuReauth = useCallback(async () => {
    if (!zebuAccount || zebuReauthing) return;
    setZebuReauthing(true);
    try {
      const result = await initiateAuth({
        brokerName: BROKER_NAME.ZEBU,
        clientId: zebuAccount.client_id,
        callbackUrl: `${window.location.origin}/oauth/callback`,
      });

      if (!result.success || !result.oauthUrl) {
        throw new Error(result.error || 'Failed to start Zebu login');
      }

      const dims = getPopupDimensions(BROKER_NAME.ZEBU);
      const popup = window.open(
        result.oauthUrl,
        'zebu-oauth',
        `width=${dims.width},height=${dims.height},scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups and try again.');
      }

      let checkClosed: ReturnType<typeof setInterval> | null = null;
      let settled = false;

      const cleanup = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', handleMessage);
        if (checkClosed) clearInterval(checkClosed);
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'ZEBU_OAUTH_SUCCESS') {
          cleanup();
          setZebuReauthing(false);
          fetchZebuAccount();
        } else if (event.data?.type === 'ZEBU_OAUTH_ERROR') {
          cleanup();
          setZebuReauthing(false);
        }
      };
      window.addEventListener('message', handleMessage);

      checkClosed = setInterval(() => {
        if (popup.closed) {
          cleanup();
          setZebuReauthing(false);
          fetchZebuAccount();
        }
      }, 1000);
    } catch (err) {
      console.error('Zebu re-auth failed:', err);
      setZebuReauthing(false);
    }
  }, [zebuAccount, zebuReauthing, initiateAuth, fetchZebuAccount]);

  const accentActive = wl
    ? 'text-zebu-600 dark:text-zebu-400'
    : 'text-blue-600 dark:text-blue-400';
  const accentBg = wl
    ? 'bg-zebu-600'
    : 'bg-blue-600';
  const accentRing = wl
    ? 'focus:ring-zebu-500'
    : 'focus:ring-blue-500';

  // Top-level flat items (always rendered as NavLinks).
  // Brokers stays top-level: non-WL all users, WL admins only (unchanged).
  const topLevelItems: NavItem[] = [
    { name: 'Dashboard', path: '/', icon: <BarChart3 className="w-4 h-4" /> },
    ...((!wl || isAdmin)
      ? [{ name: 'Brokers', path: '/brokers', icon: <Building2 className="w-4 h-4" /> }]
      : []),
    { name: 'Baskets', path: '/baskets', icon: <ShoppingBasket className="w-4 h-4" /> },
    { name: 'Allocations', path: '/allocations', icon: <Network className="w-4 h-4" /> },
    { name: 'Orders', path: '/orders', icon: <ClipboardList className="w-4 h-4" /> },
    { name: 'Positions', path: '/schedule', icon: <CalendarClock className="w-4 h-4" /> },
  ];

  // Marketplace dropdown — WL-only consumer surface.
  const marketplaceSections: NavSection[] = wl
    ? [
        {
          items: [
            { name: 'Browse Strategies', path: '/marketplace', icon: <Store className="w-4 h-4" /> },
            { name: 'My Subscriptions', path: '/subscriptions', icon: <Bell className="w-4 h-4" /> },
          ],
        },
      ]
    : [];

  // Platform dropdown — admin/AM/SA tools, grouped by audience inside.
  const platformSections: NavSection[] = [];
  if (isAdmin) {
    // Option-1 nav rule: include "Marketplace Analytics" in the Admin
    // Listings section ONLY when the caller is NOT also SuperAdmin. For
    // SuperAdmins it lives under Platform Owner below, and showing it in
    // both sections would duplicate the entry in the same dropdown.
    const listingsItems: NavItem[] = [
      { name: 'Manage Listings', path: '/admin/marketplace', icon: <Settings2 className="w-4 h-4" /> },
    ];
    if (!isSuperAdmin) {
      listingsItems.push({
        name: 'Marketplace Analytics',
        path: '/admin/marketplace-analytics',
        icon: <LineChart className="w-4 h-4" />,
      });
    }
    listingsItems.push({ name: 'Promotions', path: '/admin/promotions', icon: <Megaphone className="w-4 h-4" /> });
    platformSections.push({ heading: 'Listings', items: listingsItems });
  }
  // Account Manager section. Managed Users requires isAccountManager;
  // Terminal is also visible to SuperAdmins per existing rule.
  const acctMgrItems: NavItem[] = [];
  if (isAccountManager) {
    acctMgrItems.push({ name: 'Managed Users', path: '/admin/managed-users', icon: <Users className="w-4 h-4" /> });
  }
  if (isAccountManager || isSuperAdmin) {
    acctMgrItems.push({ name: 'Terminal', path: '/admin/account-manager/terminal', icon: <TerminalIcon className="w-4 h-4" /> });
  }
  // Marketplace Analytics for pure AccountManagers — the page + backend grant
  // AM access (per-basket scoped to their managed users), but Admins/SuperAdmins
  // already get this entry via the Listings / Platform Owner sections, so guard
  // against a duplicate entry in the same dropdown for dual-group callers.
  if (isAccountManager && !isAdmin && !isSuperAdmin) {
    acctMgrItems.push({ name: 'Marketplace Analytics', path: '/admin/marketplace-analytics', icon: <LineChart className="w-4 h-4" /> });
  }
  if (acctMgrItems.length > 0) {
    platformSections.push({ heading: 'Account Manager', items: acctMgrItems });
  }
  if (isSuperAdmin) {
    platformSections.push({
      heading: 'Platform Owner',
      items: [
        { name: 'Publisher Groups', path: '/admin/super/groups', icon: <Layers className="w-4 h-4" /> },
        { name: 'Pool Assignments', path: '/admin/super/managed-users', icon: <ShieldCheck className="w-4 h-4" /> },
        { name: 'Platform PnL', path: '/admin/super/platform-pnl', icon: <BarChart3 className="w-4 h-4" /> },
        { name: 'Audit Log', path: '/admin/super/audit', icon: <FileText className="w-4 h-4" /> },
        { name: 'Compare Baskets', path: '/admin/super/compare-baskets', icon: <GitCompareArrows className="w-4 h-4" /> },
        { name: 'Marketplace Analytics', path: '/admin/marketplace-analytics', icon: <LineChart className="w-4 h-4" /> },
        { name: 'Data Pipeline', path: '/admin/super/data-health', icon: <Activity className="w-4 h-4" /> },
      ],
    });
  }

  // Others — landing page with left-rail sub-navigation. Top-level entry
  // navigates to /others (which defaults to Timeline); the rail exposes
  // Timeline / Analytics / Expiry Days / Holidays once inside.
  const othersItems: NavItem[] = [
    { name: 'Expiry Days', path: '/others/expiry-days', icon: <CalendarClock className="w-4 h-4" /> },
    { name: 'Holidays',    path: '/others/holidays',    icon: <CalendarOff className="w-4 h-4" /> },
    { name: 'Analytics',   path: '/others/analytics',   icon: <LineChart className="w-4 h-4" /> },
    { name: 'Timeline',    path: '/others/timeline',    icon: <GitBranch className="w-4 h-4" /> },
  ];

  // Flat list used by the mobile menu (sections flattened with headings).
  // When the Marketplace dropdown is present (white-label), it sits right after
  // Dashboard — so split the first item out. Otherwise keep top-level as one
  // section so non-WL mobile nav is unchanged (no stray divider).
  const [dashboardItem, ...restTopLevel] = topLevelItems;
  const platformMobileSections = platformSections.map((s) => ({
    heading: s.heading ? `Platform · ${s.heading}` : 'Platform',
    items: s.items,
  }));
  const mobileSections: { heading?: string; items: NavItem[] }[] =
    marketplaceSections.length > 0
      ? [
          { items: [dashboardItem] },
          { heading: 'Marketplace', items: marketplaceSections.flatMap((s) => s.items) },
          { items: restTopLevel },
          ...platformMobileSections,
          { heading: 'Others', items: othersItems },
        ]
      : [
          { items: topLevelItems },
          ...platformMobileSections,
          { heading: 'Others', items: othersItems },
        ];

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (viewAsRef.current && !viewAsRef.current.contains(e.target as Node)) {
        setViewAsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Page-aware View-as gate. Baskets/Allocations enforce super_admin_only=True
  // on the backend, so showing the dropdown to Admin/AM there would let them
  // pick a target that the matching service call would silently ignore (the
  // 4 services call withActingAsIfSuperAdmin which skips the param for
  // non-SuperAdmin callers). Hiding it here keeps UX honest: Admin/AM see
  // their own data on those pages, no confusing control. SuperAdmin sees the
  // dropdown everywhere.
  //
  // Exact-or-child match so a future `/baskets-archive` route wouldn't be
  // mistakenly classified as a basket/allocation surface.
  const isPathExactOrChild = (basePath: string) =>
    location.pathname === basePath || location.pathname.startsWith(`${basePath}/`);
  const superAdminOnlyPath =
    isPathExactOrChild('/baskets') || isPathExactOrChild('/allocations');
  const dropdownAllowedOnThisPage = isSuperAdmin || !superAdminOnlyPath;
  const showViewAsSelector =
    (isAccountManager || isSuperAdmin) &&
    managedUsers.length > 0 &&
    dropdownAllowedOnThisPage;

  return (
    <header className="sticky top-0 z-30 bg-white/70 dark:bg-gray-900/70 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div className="flex items-center gap-2 shrink-0">
            {wl ? (
              <img src="/zebu-logo.svg" alt={tenantConfig.name} className="h-10 w-auto" />
            ) : (
              <>
                <Turtle className="h-7 w-7 text-blue-600" />
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  Quantleap
                </span>
              </>
            )}
          </div>

          {/* Desktop nav links */}
          <nav className="hidden lg:flex items-center gap-1">
            {topLevelItems.map((item, idx) => (
              <React.Fragment key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) => cn(
                    "flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                    isActive
                      ? cn("bg-gray-100 dark:bg-gray-800", accentActive)
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800"
                  )}
                >
                  {item.icon}
                  {item.name}
                </NavLink>
                {/* Marketplace dropdown sits immediately after Dashboard (first item). */}
                {idx === 0 && marketplaceSections.length > 0 && (
                  <NavDropdown
                    label="Marketplace"
                    icon={<Store className="w-4 h-4" />}
                    sections={marketplaceSections}
                    accentActive={accentActive}
                  />
                )}
              </React.Fragment>
            ))}
            {platformSections.length > 0 && (
              <NavDropdown
                label="Platform"
                icon={<ShieldCheck className="w-4 h-4" />}
                sections={platformSections}
                accentActive={accentActive}
                panelWidthClass="w-60"
              />
            )}
            <NavLink
              to="/others"
              className={({ isActive }) => cn(
                "flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                isActive
                  ? cn("bg-gray-100 dark:bg-gray-800", accentActive)
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800"
              )}
            >
              <MoreHorizontal className="w-4 h-4" />
              Others
            </NavLink>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {wl && zebuClientId && (
              zebuTokenValid ? (
                <div
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zebu-50 dark:bg-zebu-900/20 border border-zebu-200 dark:border-zebu-800"
                  title="Zebu access token is valid"
                >
                  <Building2 className="w-3.5 h-3.5 text-zebu-600" />
                  <span className="text-xs font-medium text-zebu-700 dark:text-zebu-300">Zebu</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{zebuClientId}</span>
                  <span className="h-2 w-2 rounded-full bg-zebu-500 ml-0.5" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleZebuReauth}
                  disabled={zebuReauthing}
                  title="Zebu access token expired — click to log in again"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-wait"
                >
                  <Building2 className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-xs font-medium text-red-700 dark:text-red-300">Zebu</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{zebuClientId}</span>
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  <span className="text-xs font-medium text-red-700 dark:text-red-300 ml-0.5">
                    {zebuReauthing ? 'Connecting…' : 'Login'}
                  </span>
                </button>
              )
            )}
            {/* "View as" selector — Phase 3.5. Visible only when the caller
                is an account-manager with a non-empty managed pool. */}
            {showViewAsSelector && (() => {
              // Single derived display label so the tooltip and the visible
              // chip text never disagree on capitalization (V10). When
              // actingAsLabel is null (e.g. cold-reload before managedUsers
              // resolves), both fall back to the same 'Managed user' string.
              const displayedLabel = actingAsLabel || 'Managed user';
              return (
              <div className="relative" ref={viewAsRef}>
                <button
                  type="button"
                  onClick={() => setViewAsOpen((v) => !v)}
                  className={cn(
                    "hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors",
                    actingAs
                      ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200"
                      : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700",
                  )}
                  title={actingAs ? `Viewing ${displayedLabel}` : 'View as managed user'}
                  aria-label="View as managed user"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium max-w-[140px] truncate">
                    {actingAs ? displayedLabel : 'View as'}
                  </span>
                  <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", viewAsOpen && "rotate-180")} />
                </button>

                {viewAsOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 flex flex-col max-h-96">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="text"
                          autoFocus
                          value={viewAsQuery}
                          onChange={(e) => setViewAsQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Escape') setViewAsOpen(false); }}
                          placeholder="Search name or client ID"
                          aria-label="Search managed users"
                          className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="py-1 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => { setActingAs(null); setViewAsOpen(false); }}
                      className={cn(
                        "w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700",
                        !actingAs ? "font-semibold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300",
                      )}
                    >
                      My account
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                    {filteredManagedUsers.length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No matching users</div>
                    )}
                    {filteredManagedUsers.map((u) => {
                      const label = u.full_name || u.email || u.managed_client_id;
                      const selected = u.managed_user_id === actingAs;
                      return (
                        <button
                          key={`${u.managed_user_id}#${u.managed_client_id}`}
                          type="button"
                          // Pass `label` as the second arg (V6) so the
                          // header chip stays human-readable on cold reload
                          // even before `fetchManagedUsers` resolves.
                          onClick={() => { setActingAs(u.managed_user_id, label); setViewAsOpen(false); }}
                          className={cn(
                            "w-full flex items-center justify-between gap-2 px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700",
                            selected ? "font-semibold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300",
                          )}
                        >
                          <span className="truncate">{label}</span>
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-400 shrink-0">{u.managed_client_id}</span>
                        </button>
                      );
                    })}
                    </div>
                  </div>
                )}
              </div>
              );
            })()}
            <KillSwitch tradingEnabled={tradingEnabled} onToggle={setTradingEnabled} />
            <ThemeToggle />

            {/* User dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((v) => !v)}
                className={cn(
                  "flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2",
                  accentRing
                )}
              >
                <div className={cn("h-8 w-8 rounded-full flex items-center justify-center text-white", accentBg)}>
                  <User className="h-4 w-4" />
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform hidden sm:block",
                  dropdownOpen && "rotate-180"
                )} />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.fullName || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {user?.email || ''}
                    </p>
                  </div>

                  {/* Settings — Quantleap only (no real settings page in WL yet). */}
                  {!wl && (
                    <button
                      onClick={() => { setDropdownOpen(false); navigate('/settings'); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <Settings className="h-4 w-4" />
                      Settings
                    </button>
                  )}
                  {/* Account — visible to every tenant. The page is the
                      self-service home for WL users (no Brokers menu), and
                      a profile/capital edit shortcut for Quantleap users. */}
                  <button
                    onClick={() => { setDropdownOpen(false); navigate('/account'); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <User className="h-4 w-4" />
                    Account
                  </button>

                  <div className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1">
                    <button
                      onClick={() => { setDropdownOpen(false); logout(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
            >
              <span className="sr-only">Open menu</span>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* "Acting as" banner — protects operator against forgetting they're
          viewing someone else's data. Sticky with the header. */}
      {actingAs && (
        <div className="border-t border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
          <div className="px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <Eye className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium">
                Viewing <span className="font-semibold">{actingAsLabel || actingAs}</span>
                <span className="hidden sm:inline text-amber-700/80 dark:text-amber-300/80 font-normal ml-2">
                  — all Schedule / Positions / Orders / Analytics are scoped to this user
                </span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => setActingAs(null)}
              className="flex items-center gap-1 text-xs font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 px-2 py-1 rounded transition-colors"
            >
              <XIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Return to my account</span>
              <span className="sm:hidden">Exit</span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile nav */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 px-4 py-2 space-y-1">
          {mobileSections.map((section, sIdx) => (
            <div key={sIdx} className={sIdx > 0 ? 'pt-2 mt-2 border-t border-gray-200 dark:border-gray-700' : ''}>
              {section.heading && (
                <div className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {section.heading}
                </div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                    isActive
                      ? cn("bg-gray-100 dark:bg-gray-800", accentActive)
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800"
                  )}
                >
                  {item.icon}
                  {item.name}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;